import React, { useState, useDeferredValue } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import Input from '@/components/Input';
import Checkbox from '@/components/Checkbox';
import { Tag } from '@/components/Tag';
import { MdCancel, MdKeyboardArrowDown } from 'react-icons/md';

// 汎用的なオプション型
export interface MultiSelectOption {
  id: number | string;
  name: string;
  value?: string | number; // 実際の値（idと異なる場合）
}

interface MultiSelectPopoverProps {
  options: MultiSelectOption[];
  value: (number | string)[];
  onChange: (value: (number | string)[]) => void;
  placeholder?: string;
  searchable?: boolean;
  align?: 'start' | 'end';
  columns?: 1 | 2 | 3;
  disabled?: boolean;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  // list はデザインシステムの MultiSelect 準拠（余白なし・行ごとに px-sm py-xs・選択行 bg-gray）
  variant?: 'grid' | 'list';
  // false にすると固定幅 724px で開く。既定はトリガー(入力欄)と同じ幅
  matchTriggerWidth?: boolean;
  // 検索語を外部制御したい場合に指定する（サーバー検索など、options 自体を呼び出し側で絞り込む場合）
  query?: string;
  onQueryChange?: (query: string) => void;
  // 指定すると選択タグの代わりに件数サマリを表示する。大量選択でトリガーが横長になるのを避ける用途 (Issue #3484)
  renderSelectedSummary?: (count: number) => React.ReactNode;
}

export function MultiSelectPopover({
  options,
  value,
  onChange,
  placeholder = '選択してください',
  searchable = false,
  align = 'start',
  columns = 3,
  disabled = false,
  className = '',
  header,
  footer,
  variant = 'grid',
  matchTriggerWidth = true,
  query: externalQuery,
  onQueryChange,
  renderSelectedSummary,
}: MultiSelectPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState('');
  const isQueryControlled = externalQuery !== undefined;
  const deferredInternalQuery = useDeferredValue(internalQuery);
  const query = isQueryControlled ? externalQuery : internalQuery;
  const handleQueryChange = (value: string) => {
    setInternalQuery(value);
    if (onQueryChange) onQueryChange(value);
  };

  // 選択されているオプションを取得
  const selectedOptions = options.filter((option) => value.includes(option.id));

  // 検索フィルタリング（外部制御時は options 自体が絞り込み済みのためフィルタしない）
  const filteredOptions =
    searchable && !isQueryControlled
      ? options.filter((option) =>
          option.name.toLowerCase().includes(deferredInternalQuery.toLowerCase())
        )
      : options;

  // チェックボックスの変更ハンドラ
  const handleCheckboxChange = (optionId: number | string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionId]);
    } else {
      onChange(value.filter((id) => id !== optionId));
    }
  };

  // 値を削除
  const removeValue = (optionId: number | string) => {
    onChange(value.filter((id) => id !== optionId));
  };

  // グリッドのカラム数に応じたクラス
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
  };

  const isList = variant === 'list';

  return (
    <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className={`relative flex h-xxl w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white px-sm ${
            isOpen ? 'outline outline-2 outline-offset-[-2px] outline-primary' : ''
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
        >
          <div className="hidden-scrollbar flex flex-1 gap-2 overflow-x-auto pr-2 text-nowrap">
            {selectedOptions.length > 0 && renderSelectedSummary ? (
              <span className="text-body-sm text-main">
                {renderSelectedSummary(selectedOptions.length)}
              </span>
            ) : selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <div key={option.id} className="shrink-0">
                  <Tag color="default" className="flex items-center gap-xxs">
                    {option.name}
                    {!disabled && (
                      <button
                        type="button"
                        className="flex size-5 items-center justify-center rounded-full border-none bg-transparent p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeValue(option.id);
                        }}
                      >
                        <MdCancel className="size-icon-xs fill-main opacity-50" />
                      </button>
                    )}
                  </Tag>
                </div>
              ))
            ) : (
              <span className="text-body-sm text-placeholder">{placeholder}</span>
            )}
          </div>
          {selectedOptions.length > 0 && !renderSelectedSummary && (
            <div className="pointer-events-none absolute inset-y-2 right-8 w-8 bg-gradient-to-l from-white to-transparent" />
          )}
          <MdKeyboardArrowDown
            className={`size-icon-xs shrink-0 text-main transition-transform duration-500 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={16}
        className={`z-[9999] max-w-[calc(100vw-2rem)] ${
          isList ? 'overflow-hidden rounded-sm p-0 shadow-layer3' : 'p-4'
        } ${matchTriggerWidth ? 'w-(--radix-popover-trigger-width)' : 'w-[724px]'}`}
      >
        <div className={isList ? '' : 'space-y-4'}>
          {header}
          {searchable && (
            <div className={isList ? 'p-sm' : ''}>
              <Input
                search
                className="w-full"
                placeholder="フリーワードで検索"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
              />
            </div>
          )}

          <div className="max-h-[264px] overflow-y-auto">
            <div className={isList ? 'flex flex-col' : `grid ${gridCols[columns]} gap-3`}>
              {filteredOptions.map((option) => {
                const checked = value.includes(option.id);
                return (
                  <div
                    key={option.id}
                    className={
                      isList
                        ? `px-sm py-xs text-body-sm hover:bg-gray [&>label]:w-full [&>label]:py-0 ${checked ? 'bg-gray' : ''}`
                        : ''
                    }
                  >
                    <Checkbox
                      value={option.id.toString()}
                      checked={checked}
                      onChange={(next) => handleCheckboxChange(option.id, next)}
                    >
                      <span className="font-normal">{option.name}</span>
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          </div>
          {footer}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// デフォルトエクスポート（既存コードとの互換性のため）
export default MultiSelectPopover;
